import React from 'react';
import { LoadingSpinner } from '@/components';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from '@chakra-ui/react';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { WorkplanYear } from '@/wpt-lib/interfaces/Workplan';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import PlaceholderDashboard from '@/components/PlaceholderDashboard/PlaceholderDashboard';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';

const WorkplanLoader = () => {
    const { currentAssignment } = useAssignment();

    const { makeRequest, responseData, loading, error } = useGetRequest<WorkplanYear[]>();

    const { navigatePath } = useNavigateOptions();

    useAsyncEffect(async () => {
        if (!currentAssignment) return;

        try {
            const response = await makeRequest(`workplan/years`, {
                params: {
                    assignment: currentAssignment.id,
                },
            });

            const workplanYears = response.data;

            /* No workplans. */
            if (workplanYears.length === 0) return;

            const currentYear = new Date().getFullYear();

            const currentFuture = workplanYears
                .filter((w) => w.year >= currentYear)
                .sort((a, b) => (a.year === b.year ? (a.historical ? 1 : -1) : a.year - b.year));

            let workplan: WorkplanYear | undefined;

            if (currentFuture.length > 0) {
                /* Current or first future workplan. */
                workplan = currentFuture[0];
            } else {
                /* Latest previous workplan. */
                workplanYears.sort((a, b) => (b.year === a.year ? (a.historical ? -1 : 1) : b.year - a.year));
                workplan = workplanYears[workplanYears.length - 1];
            }

            if (workplan) {
                navigatePath(workplan.id, Math.max(...workplan.versions));
            }

            /* First future workplan. */
        } catch {
            // Handled by hook.
        }
    }, [currentAssignment]);

    if (loading)
        return (
            <Box m={10}>
                <LoadingSpinner />
            </Box>
        );

    if (error)
        return (
            <Box m={10}>
                <Alert status="error">
                    <AlertIcon />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>Could not load workplan data. Please refresh page.</AlertDescription>
                </Alert>
            </Box>
        );

    if (responseData && responseData.length === 0) {
        return <PlaceholderDashboard />;
    }

    return null;
};

export default WorkplanLoader;
